import { useState, ImgHTMLAttributes } from "react";

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> {
  src: string;
  alt: string;
  /**
   * CRITICAL: Set to true if this is a print file that should NOT be optimized
   */
  isPrint?: boolean;
}

/**
 * OptimizedImage Component
 * 
 * Renders images with lazy loading and error handling.
 * Print files are rendered without any optimization.
 */
export function OptimizedImage({
  src,
  alt,
  isPrint = false,
  className = "",
  loading = "lazy",
  ...props
}: OptimizedImageProps) {
  const [hasError, setHasError] = useState(false);

  if (!src || typeof src !== 'string') {
    return (
      <div className={`flex items-center justify-center bg-muted ${className}`} {...props}>
        <span className="text-muted-foreground text-sm">Bilden kunde inte laddas</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-muted ${className}`} {...props}>
        <span className="text-muted-foreground text-sm">Bilden kunde inte laddas</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`w-full h-full object-cover object-center ${className}`}
      loading={loading}
      decoding="async"
      onError={() => setHasError(true)}
      {...props}
    />
  );
}
